import { Phone, MapPin, Truck, CheckCircle2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { useReveal } from "@/hooks/useReveal";

const steps = [
  {
    icon: Phone,
    title: "Call Us",
    text: "Tell us where you are and what's happening. We'll quote a fair price on the phone.",
  },
  {
    icon: MapPin,
    title: "We Dispatch",
    text: "A driver heads your way with the right equipment for your vehicle and situation.",
  },
  {
    icon: Truck,
    title: "Safe Recovery",
    text: "We load, winch, jump-start or unlock — carefully and professionally.",
  },
  {
    icon: CheckCircle2,
    title: "Back on the Road",
    text: "We drop you at home, a shop, or wherever you need to go. Simple.",
  },
];

export default function Process() {
  const headRef = useReveal();
  return (
    <section className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(250,204,21,0.10) 0%, rgba(0,0,0,0) 55%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={headRef} className="lux-reveal flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-3xl">
            <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
              <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
              How It Works
            </div>
            <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              One call. Four steps. <span className="text-yellow-400">Done.</span>
            </h2>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="process-call-btn"
            className="hidden lg:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>

        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => {
            const I = s.icon;
            return (
              <li
                key={s.title}
                className={`relative lux-card lux-glass rounded-2xl p-6 sm:p-7 lux-reveal lux-reveal-delay-${(i % 4) + 1}`}
              >
                {/* Step number */}
                <div className="flex items-center justify-between">
                  <span
                    className="font-display font-black text-yellow-400/25 text-6xl leading-none tracking-tighter"
                    aria-hidden="true"
                  >
                    0{i + 1}
                  </span>
                  <div className="h-10 w-10 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                    <I className="h-5 w-5 text-yellow-400" strokeWidth={2} />
                  </div>
                </div>
                <div className="mt-5 font-display font-black uppercase text-white text-lg sm:text-xl tracking-tight">
                  {s.title}
                </div>
                <div className="mt-2 text-zinc-400 text-sm font-light leading-relaxed">{s.text}</div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
